/**
 * Минимальный хук загрузки данных.
 *
 * Вместо TanStack Query: экранов немного, кэш между ними почти не нужен, а
 * лишняя зависимость в 40 КБ ради трёх запросов на экран — плохая сделка.
 * Когда понадобятся инвалидация и фоновое обновление, это заменяется целиком.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '@allswipes/client';

export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

export function useResource<T>(load: () => Promise<T>, deps: unknown[] = []): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    const request = ++latest.current;
    setLoading(true);
    setError(null);
    load()
      .then((result) => {
        // Игнорируем ответ устаревшего запроса: иначе быстрый переход между
        // акциями показывает данные предыдущей.
        if (request === latest.current) setData(result);
      })
      .catch((err: unknown) => {
        if (request !== latest.current) return;
        setError(err instanceof ApiError ? err.message : 'Не удалось загрузить данные');
      })
      .finally(() => {
        if (request === latest.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}
